/** @jsxImportSource theme-ui */
import React, { useState, ChangeEvent } from "react";
import { Box, Input, Text } from "theme-ui";

type SearchFieldProps = {
    fetchResults: (query: string) => Promise<string[]>;
    onSelect: (selectedItem: string) => void;
    placeholderMessage: string;
};

const SearchField: React.FC<SearchFieldProps> = ({ fetchResults, onSelect, placeholderMessage }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setQuery(inputValue);

        if (inputValue.length >= 3) {
            const fetchedResults = await fetchResults(inputValue);
            setResults(fetchedResults);
            setIsOpen(true);
        } else {
            setResults([]);
            setIsOpen(false);
        }
    };

    const handleResultClick = (result: string) => {
        setQuery(result);
        setIsOpen(false);
        onSelect(result); // Notify parent of the selection
    };

    const handleBlur = () => {
        setTimeout(() => setIsOpen(false), 200);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: ["100%", "300px"], paddingTop: "20px", alignItems: "center" }}>

            <Box sx={{ position: "relative", width: "100%" }}>
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        "::after": {
                            content: query ? '""' : `"${placeholderMessage}"`,
                            position: "absolute",
                            left: "16px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "gray",
                            fontSize: "16px", // Ensure 16px font size to prevent zoom on iOS
                            pointerEvents: "none",
                        },
                    }}
                >
                    <Input
                        sx={{
                            width: "100%",
                            borderRadius: "35px",
                            border: "1px solid #ccc", // Light gray border
                            padding: ["12px", "16px"],
                            fontSize: "16px", // Match dropdown text size
                            color: "primaryText", // Match dropdown text color
                            fontFamily: "body", // Ensure consistent font family
                            height: ["40px", "50px"],
                            "::placeholder": {
                                color: "transparent",
                            },
                            ":focus": {
                                outline: "none",
                                borderColor: "#ccc",
                            },
                            ":hover": {
                                borderColor: "#ccc",
                            },
                        }}
                        placeholder={placeholderMessage}
                        value={query}
                        onChange={handleChange}
                        onFocus={() => isOpen || results.length ? setIsOpen(true) : null}
                        onBlur={handleBlur}
                    />
                </Box>
                {query.length > 0 && query.length < 3 && (
                    <Text sx={{ fontSize: 0, color: "secondaryText", mt: 1, pl: 2 }}>
                        Type at least 3 characters to see results
                    </Text>
                )}
                {isOpen && results.length > 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "100%",
                            left: "0",
                            width: "100%",
                            backgroundColor: "white",
                            border: "1px solid #ccc", // Light gray border for consistency
                            borderRadius: "15px",
                            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                            zIndex: 10,
                            maxHeight: "200px",
                            overflowY: "auto",
                        }}
                    >
                        {results.map((result, index) => (
                            <Box
                                key={index}
                                sx={{
                                    padding: "12px 16px",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    color: "text",
                                    ":hover": {
                                        backgroundColor: "#f0f0f0",
                                    },
                                }}
                                onClick={() => handleResultClick(result)}
                            >
                                <Text
                                    sx={{
                                        color: "primaryText",
                                        fontSize: "16px",
                                    }}
                                >
                                    {result}
                                </Text>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SearchField;